<template>
  <div class="group-c-details">
    <a-table
      :columns="columns"
      :data-source="tableData"
      :loading="loading"
      :pagination="paginationConfig"
      :scroll="{ x: 1000 }"
      @change="handleTableChange"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'date'">
          <span :class="getDateClass(record.date)">{{ formatDate(record.date) }}</span>
        </template>
        <template v-else-if="column.key === 'messageSent'">
          <span>{{ formatNumber(record.messageSent || 0) }}</span>
        </template>
        <template v-else-if="column.key === 'messageClicks'">
          <span>{{ formatNumber(record.messageClicks || 0) }}</span>
        </template>
        <template v-else-if="column.key === 'hospitalDetailClicks'">
          <span>{{ formatNumber(record.hospitalDetailClicks || 0) }}</span>
        </template>
        <template v-else-if="column.key === 'consultationClicks'">
          <span>{{ formatNumber(record.consultationClicks || 0) }}</span>
        </template>
        <template v-else-if="column.key === 'reservations'">
          <span :class="{ 'reservation-highlight': record.reservations > 0 }">
            {{ formatNumber(record.reservations || 0) }}
          </span>
        </template>
      </template>

      <template #summary>
        <a-table-summary fixed>
          <a-table-summary-row class="summary-row">
            <a-table-summary-cell :index="0" align="center">
              <strong>합계</strong>
            </a-table-summary-cell>
            <a-table-summary-cell :index="1" align="right">
              <strong class="summary-value">{{ formatNumber(calculateSum('messageSent')) }}</strong>
            </a-table-summary-cell>
            <a-table-summary-cell :index="2" align="right">
              <strong class="summary-value">{{ formatNumber(calculateSum('messageClicks')) }}</strong>
            </a-table-summary-cell>
            <a-table-summary-cell :index="3" align="right">
              <strong class="summary-value">{{ formatNumber(calculateSum('hospitalDetailClicks')) }}</strong>
            </a-table-summary-cell>
            <a-table-summary-cell :index="4" align="right">
              <strong class="summary-value">{{ formatNumber(calculateSum('consultationClicks')) }}</strong>
            </a-table-summary-cell>
            <a-table-summary-cell :index="5" align="right">
              <strong class="summary-value">{{ formatNumber(calculateSum('reservations')) }}</strong>
            </a-table-summary-cell>
          </a-table-summary-row>
        </a-table-summary>
      </template>
    </a-table>
  </div>
</template>

<script>
import { ref, computed, watch, getCurrentInstance } from 'vue';
import { Table } from 'ant-design-vue';
import dayjs from 'dayjs';
import reportApi from '../api';
import { useStore } from 'vuex';
import { getDayOfWeek } from '../utils';

const { Summary } = Table;

export default {
  name: 'GroupCDetails',
  components: {
    ATable: Table,
    ATableSummary: Summary,
    ATableSummaryRow: Summary.Row,
    ATableSummaryCell: Summary.Cell,
  },
  props: {
    reportId: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const store = useStore();
    const instance = getCurrentInstance();
    const api = reportApi(instance.proxy.$apiInstance, store);

    const loading = ref(false);
    const tableData = ref([]);
    const pagination = ref({
      current: 1,
      pageSize: 100,
      total: 0,
      totalPages: 0
    });
    const sortBy = ref('date');
    const sortOrder = ref('asc');

    // 테이블 컬럼 정의
    const columns = [
      {
        title: '일자',
        dataIndex: 'date',
        key: 'date',
        width: 120,
        align: 'center',
        fixed: 'left',
      },
      {
        title: '메시지 발송 건수',
        dataIndex: 'messageSent',
        key: 'messageSent',
        width: 140,
        align: 'right',
      },
      {
        title: '메시지 클릭 수',
        dataIndex: 'messageClicks',
        key: 'messageClicks',
        width: 130,
        align: 'right',
      },
      {
        title: '병원 상세 페이지 클릭 수',
        dataIndex: 'hospitalDetailClicks',
        key: 'hospitalDetailClicks',
        width: 180,
        align: 'right',
      },
      {
        title: '상담하기 클릭 수',
        dataIndex: 'consultationClicks',
        key: 'consultationClicks',
        width: 150,
        align: 'right',
      },
      {
        title: '병원 예약',
        dataIndex: 'reservations',
        key: 'reservations',
        width: 110,
        align: 'right',
      },
    ];

    // 페이지네이션 설정 (100건 고정)
    const paginationConfig = computed(() => ({
      current: pagination.value.current,
      pageSize: 100,
      total: pagination.value.total,
      showSizeChanger: false,
      showQuickJumper: false,
      showTotal: (total) => `전체 ${total.toLocaleString()}건`,
    }));

    // 날짜 포맷팅 (요일 포함)
    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = dayjs(dateString);
      const dayOfWeek = ['일', '월', '화', '수', '목', '금', '토'][date.day()];
      return `${date.format('YYYY-MM-DD')}(${dayOfWeek})`;
    };

    // 숫자 포맷팅 (천단위 콤마)
    const formatNumber = (num) => {
      if (num === null || num === undefined) return '0';
      return num.toLocaleString();
    };

    // 합계 계산
    const calculateSum = (field) => {
      if (!tableData.value || tableData.value.length === 0) return 0;
      return tableData.value.reduce((sum, item) => sum + (item[field] || 0), 0);
    };

    // 날짜 컬럼 색상 클래스
    const getDateClass = (dateStr) => {
      const dayOfWeek = getDayOfWeek(dateStr);
      if (dayOfWeek === 0) return 'date-sunday'; // 일요일
      if (dayOfWeek === 6) return 'date-saturday'; // 토요일
      return '';
    };

    // 데이터 조회
    const fetchData = async () => {
      if (!props.reportId) {
        console.log('🚫 [GroupCDetails] reportId가 없습니다:', props.reportId);
        return;
      }

      console.log('🔍 [GroupCDetails] API 호출 시작 - reportId:', props.reportId);
      loading.value = true;
      try {
        await api.getGroupDetails(
          {
            reportId: props.reportId,
            group: 'C',
            page: pagination.value.current,
            limit: pagination.value.pageSize,
            sortBy: sortBy.value,
            sortOrder: sortOrder.value
          },
          (response) => {
            console.log('✅ [GroupCDetails] API 응답 성공:', response);

            if (response.data && Array.isArray(response.data)) {
              // 날짜 오름차순 정렬 (9/15 → 10/31)
              tableData.value = response.data.sort((a, b) => {
                return new Date(a.date) - new Date(b.date);
              });
              console.log('📊 [GroupCDetails] 테이블 데이터 설정:', tableData.value.length, '건');

              if (response.pagination) {
                pagination.value.current = parseInt(response.pagination.page) || 1;
                pagination.value.pageSize = parseInt(response.pagination.limit) || 20;
                pagination.value.total = response.pagination.total || 0;
                pagination.value.totalPages = response.pagination.totalPages || 0;
                console.log('📄 [GroupCDetails] 페이지네이션:', pagination.value);
              }
            }
          },
          (error) => {
            console.error('❌ [GroupCDetails] API 호출 실패:', error);
            console.error('❌ [GroupCDetails] 에러 상세:', error.response?.data);
          }
        );
      } finally {
        loading.value = false;
      }
    };

    // 테이블 변경 핸들러 (페이지네이션)
    const handleTableChange = (paginationParams) => {
      // 페이지네이션 업데이트 (페이지만 변경, pageSize는 100 고정)
      if (paginationParams) {
        pagination.value.current = paginationParams.current;
      }

      // 데이터 재조회
      fetchData();
    };

    // reportId 변경 감지
    watch(() => props.reportId, (newReportId, oldReportId) => {
      console.log('🔄 [GroupCDetails] reportId 변경 감지:', {
        old: oldReportId,
        new: newReportId
      });
      if (newReportId) {
        pagination.value.current = 1; // 페이지 초기화
        fetchData();
      }
    }, { immediate: true });

    return {
      columns,
      tableData,
      loading,
      paginationConfig,
      formatDate,
      formatNumber,
      calculateSum,
      getDateClass,
      handleTableChange,
    };
  },
};
</script>

<style scoped>
.group-c-details {
  padding: 20px;
  background: #fff;
  border-radius: 8px;
}

:deep(.ant-table-wrapper) {
  background: #fff;
}

:deep(.ant-table-thead > tr > th) {
  background: #fafafa;
  font-weight: 600;
  color: #262626;
  font-size: 13px;
  border-bottom: 2px solid #e8e8e8;
}

:deep(.ant-table-tbody > tr:hover > td) {
  background: #f5f9ff;
}

/* 날짜 컬럼 색상 */
.date-saturday {
  color: #1890ff;
  font-weight: 600;
}

.date-sunday {
  color: #f5222d;
  font-weight: 600;
}

/* 예약 강조 */
.reservation-highlight {
  color: #f5222d !important;
  font-weight: 700 !important;
  background: #fff1f0;
  padding: 2px 6px;
  border-radius: 4px;
}

/* 페이지네이션 스타일 */
:deep(.ant-pagination) {
  margin-top: 24px;
  text-align: center;
}

:deep(.ant-pagination-total-text) {
  font-weight: 500;
  color: #262626;
}

/* 합계 행 스타일 */
:deep(.summary-row) {
  background: #e6f7ff !important;
  border-top: 2px solid #1890ff;
}

:deep(.summary-row td) {
  font-weight: 600 !important;
  color: #0050b3 !important;
  padding: 12px 8px !important;
}

.summary-value {
  font-size: 14px;
  color: #0050b3;
}
</style>
